import React, { useState, useEffect, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import usePositionPopup from 'components/PositionPopup';
import Styled from './styles';

export interface MenuProps extends React.ComponentPropsWithoutRef<'div'> {
  /**
   * styled-components "as" prop
   * https://styled-components.com/docs/api#as-polymorphic-prop
   * */
  as?: React.ComponentType<any> | undefined;
  /** Any element ref that triggers the menu. */
  triggerEl?: React.RefObject<HTMLElement>;
  /** To render the menu contents. */
  children: React.ReactNode;
  /** Control `menu` open state. */
  isOpen?: boolean;
  /** Keeps the menu mounted, when isOpen is false */
  keepMounted?: boolean;
  /** Control `menu` container style. */
  style?: React.CSSProperties;
  /** Override `menu` positioning options based on popper (https://popper.js.org/docs/v2/). */
  positioningOptions?: {};
  /** Have `menu` render in portal. */
  usePortal?: boolean;
  /** Specify container in which `menu` is rendered in portal. */
  portalContainer?: Element | DocumentFragment | {
    current?: Element | DocumentFragment
  };
  /**
   * Callback fired when the component requests to be closed.
   *
   * @param {object} event The event source of the callback.
   */
  onClose?: (arg: React.MouseEvent) => void;
}

export type MenuRef = {
  positionMenu: () => void;
  wrapperRef: HTMLElement;
};

/** Position popup with respect to a target and handle closing/opening.
 * Any other props than listed will be provided to the root element.
 * To re-position menu on changes to children or target element, please use `positionMenu` callback using component ref.
 * The forwarded ref is available as 'wrapperRef'.
 */
const Menu = React.forwardRef<MenuRef, MenuProps>((props: MenuProps, ref) => {
  const {
    triggerEl,
    children,
    isOpen,
    positioningOptions,
    usePortal,
    portalContainer,
    onClose,
    style,
    keepMounted,
    ...otherProps
  } = props;
  const [popperElement, setPopperElement] = useState(null);
  const { styles, attributes, forceUpdate } = usePositionPopup(
    triggerEl && triggerEl.current,
    popperElement,
    positioningOptions
  );
  const menuContent = (
    <Styled.MenuContainer
      ref={setPopperElement}
      {...attributes.popper}
      style={{
        ...styles.popper,
        ...style
      }}
      {...otherProps}
    >
      {children}
    </Styled.MenuContainer>
  );
  const handleClickOutsideMenu = (event) => {
    if (!isOpen || (popperElement && popperElement.contains(event.target)) || (triggerEl.current && triggerEl.current.contains(event.target))) {
      return;
    }
    onClose && onClose(event);
  };

  useImperativeHandle(ref, () => ({
    wrapperRef: popperElement,
    positionMenu: () => {
      forceUpdate && forceUpdate();
    }
  }), [popperElement, forceUpdate]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutsideMenu);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideMenu);
    };
  }, [isOpen, popperElement]);

  const resolvedPortalContainer = portalContainer instanceof Element || portalContainer instanceof DocumentFragment ? portalContainer : portalContainer.current;

  return (
    <>
      {(keepMounted || isOpen) && usePortal &&
        createPortal(
          menuContent,
          resolvedPortalContainer
        )}
      {(keepMounted || isOpen) && !usePortal && menuContent}
    </>
  );
});

Menu.displayName = 'Menu';

Menu.defaultProps = {
  triggerEl: { current: undefined },
  isOpen: false,
  style: {},
  positioningOptions: {},
  usePortal: false,
  portalContainer: document.body,
  onClose: undefined,
  keepMounted: false,
  as: undefined
};

export default Menu;
